import { withPrefix } from 'gatsby'
import {
  deriveSeoFromResult,
  brandTitle,
  resolveOpenGraphImage,
} from './seo-utils'
import structuredDataPage from './structured-data-page.cjs'
import structuredDataUtils from './structured-data.cjs'

const { buildStructuredDataFromPageData, getPageKind } = structuredDataPage
const { safeJsonStringify } = structuredDataUtils

function normalizePathname(p) {
  const u = new URL(p, window.location.origin)
  let s = u.pathname.replace(/\/+/g, '/')
  if (!s.startsWith('/')) s = `/${s}`
  if (s === '/404.html/' || s === '/500.html/') return s.slice(0, -1)
  if (s === '/404.html' || s === '/500.html') return s
  return s === '/' ? '/' : s.endsWith('/') ? s : `${s}/`
}

function selByName(name) {
  return document.head.querySelector(`meta[name="${CSS.escape(name)}"]`)
}
function selByProp(prop) {
  return document.head.querySelector(`meta[property="${CSS.escape(prop)}"]`)
}
function upsertMetaName(name, content) {
  const existing = selByName(name)
  if (content == null || content === '') {
    if (existing) existing.parentNode.removeChild(existing)
    return
  }
  if (existing) {
    if (existing.getAttribute('content') !== String(content))
      existing.setAttribute('content', String(content))
  } else {
    const m = document.createElement('meta')
    m.setAttribute('name', name)
    m.setAttribute('content', String(content))
    document.head.appendChild(m)
  }
}
function upsertMetaProp(prop, content) {
  const existing = selByProp(prop)
  if (content == null || content === '') {
    if (existing) existing.parentNode.removeChild(existing)
    return
  }
  if (existing) {
    if (existing.getAttribute('content') !== String(content))
      existing.setAttribute('content', String(content))
  } else {
    const m = document.createElement('meta')
    m.setAttribute('property', prop)
    m.setAttribute('content', String(content))
    document.head.appendChild(m)
  }
}

function upsertCanonical(href) {
  const canonicals = Array.from(
    document.head.querySelectorAll('link[rel="canonical"]')
  )
  const existing = canonicals.shift()

  canonicals.forEach((node) => node.parentNode.removeChild(node))

  if (existing) {
    if (existing.getAttribute('href') !== href)
      existing.setAttribute('href', href)
    return
  }

  const link = document.createElement('link')
  link.setAttribute('rel', 'canonical')
  link.setAttribute('href', href)
  document.head.appendChild(link)
}

function resolveAbsoluteUrl(value, base) {
  if (value == null) return ''
  const raw = String(value).trim()
  if (!raw) return ''
  try {
    return new URL(raw).toString()
  } catch {
    try {
      return new URL(raw, base).toString()
    } catch {
      return raw
    }
  }
}

export function applySeoToDocument(seo, brand = 'Harbor Capital') {
  const fullTitle = brandTitle(seo.title, brand)
  if (fullTitle && document.title !== fullTitle) document.title = fullTitle

  upsertMetaName('description', seo.description ?? '')
  upsertMetaName(
    'keywords',
    Array.isArray(seo.keywords) ? seo.keywords.join(', ') : (seo.keywords ?? '')
  )
  if (seo.institutional) upsertMetaName('data-institutional', 'true')
  else upsertMetaName('data-institutional', '')
  upsertMetaName(
    'robots',
    seo.robots ||
      'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
  )

  const og = seo.openGraph || {}
  const canonicalPath = normalizePathname(window.location.pathname)
  const canonicalUrl = resolveAbsoluteUrl(canonicalPath, window.location.origin)
  upsertCanonical(canonicalUrl)
  const openGraphImage = resolveOpenGraphImage(
    og.image,
    window.location.origin
  )
  const openGraphTitle = og.title || fullTitle || ''
  const openGraphDescription = og.description || seo.description || ''
  const ogPairs = {
    'og:type': og.type || 'website',
    'og:title': openGraphTitle,
    'og:description': openGraphDescription,
    'og:url': canonicalUrl,
    'og:image': openGraphImage,
  }
  Object.entries(ogPairs).forEach(([prop, content]) =>
    upsertMetaProp(prop, content)
  )

  const twitterPairs = {
    'twitter:card': openGraphImage ? 'summary_large_image' : 'summary',
    'twitter:title': openGraphTitle,
    'twitter:description': openGraphDescription,
    'twitter:image': openGraphImage,
  }
  Object.entries(twitterPairs).forEach(([name, content]) =>
    upsertMetaName(name, content)
  )
}

async function getPageDataViaLoader(pathname) {
  const loader = window.___loader
  if (typeof loader?.loadPage !== 'function') return null

  try {
    const loaded = await loader.loadPage(pathname)
    if (loaded?.page?.result) return loaded.page
    if (loaded?.json?.result) return loaded.json
    if (!loaded?.json) return null

    return {
      ...(loaded?.page || {}),
      path: loaded?.page?.path || pathname,
      result: loaded.json,
      staticQueryResults: loaded.staticQueryResults || {},
      staticQueryHashes:
        loaded?.page?.staticQueryHashes || loaded?.json?.staticQueryHashes || [],
    }
  } catch {
    // intentionally empty: ignore errors
  }
  return null
}

async function getPageDataViaFetch(pathname) {
  try {
    const seg =
      pathname === '/'
        ? 'index'
        : pathname.replace(/^\//, '').replace(/\/$/, '')
    const url = withPrefix(`/page-data/${seg}/page-data.json`)
    const resp = await fetch(url, {
      credentials: 'same-origin',
      cache: 'force-cache',
    })
    if (!resp.ok) return null
    return await resp.json()
  } catch {
    return null
  }
}

async function loadPageDataForPath(pathname) {
  const n = normalizePathname(pathname)
  let pageData = await getPageDataViaLoader(n)
  if (!pageData?.componentChunkName) pageData = await getPageDataViaFetch(n)
  if (!pageData) {
    await new Promise((r) => setTimeout(r, 0))
    pageData =
      (await getPageDataViaLoader(n)) || (await getPageDataViaFetch(n))
  }
  return { n, pageData }
}

async function loadStaticQueryResults(pageData) {
  const results = new Map()
  if (getPageKind(pageData) !== 'suite') return results

  const loaderResults = pageData?.staticQueryResults || {}
  await Promise.all(
    (pageData?.staticQueryHashes || []).map(async (hash) => {
      if (loaderResults[hash]) {
        results.set(hash, loaderResults[hash])
        return
      }
      try {
        const response = await fetch(withPrefix(`/page-data/sq/d/${hash}.json`), {
          credentials: 'same-origin',
          cache: 'force-cache',
        })
        if (response.ok) results.set(hash, await response.json())
      } catch {
        // A missing static query is handled by the page-data graph adapter.
      }
    })
  )
  return results
}

export function applyStructuredDataToDocument(pageData, staticQueryResults) {
  const scripts = Array.from(
    document.head.querySelectorAll('script[data-hca-structured-data]')
  )
  const structured = buildStructuredDataFromPageData(pageData, {
    readStaticQuery: (hash) => staticQueryResults?.get(hash),
  })

  if (!structured?.graph) {
    scripts.forEach((script) => script.parentNode.removeChild(script))
    return
  }

  const script = scripts.shift() || document.createElement('script')
  scripts.forEach((duplicate) => duplicate.parentNode.removeChild(duplicate))
  script.setAttribute('type', 'application/ld+json')
  script.setAttribute('data-hca-structured-data', structured.kind)
  script.textContent = safeJsonStringify(structured.graph)
  if (!script.parentNode) document.head.appendChild(script)
}

export async function applySeoForPath(pathname, brand = 'Harbor Capital') {
  const { n, pageData } = await loadPageDataForPath(pathname)
  if (!pageData?.result) {
    if (normalizePathname(window.location.pathname) !== n) return
    applyStructuredDataToDocument(null)
    return
  }
  const staticQueryResults = await loadStaticQueryResults(pageData)
  if (normalizePathname(window.location.pathname) !== n) return

  const seo = deriveSeoFromResult(pageData.result, n)
  const applyPageHead = () => {
    if (normalizePathname(window.location.pathname) !== n) return
    applySeoToDocument(seo, brand)
    applyStructuredDataToDocument(pageData, staticQueryResults)
  }
  applyPageHead()
  requestAnimationFrame(applyPageHead)
}
